import { AssetType, DeviceFamily, Placement } from '@amzn/fire-tv-campaign-automation-service';
import { useEffect, useRef } from 'react';
import { getDefaultAssetState, AssetContextState } from 'src/contexts/AssetContext';
import { useSelection } from 'src/contexts/SelectionContext';
import { AssetStatus, InputType, AssetState } from 'src/types';

/**
 * Custom hook to handle asset state reset when locale changes
 *
 * Watches for locale changes and resets assets based on their input type:
 * - Search-based assets are cleared (locale-specific)
 * - Upload/URL-based assets are preserved (locale-agnostic)
 * - Video assets are preserved (locale-agnostic)
 *
 * @param assetStates - Current asset states
 * @param updateAssetState - Function to update asset state
 */
export const useLocaleChangeAssetReset = (
  assetStates: AssetContextState,
  updateAssetState: (assetType: AssetType, state: Partial<AssetState>) => void,
) => {
  const { locale, deviceFamily, placement } = useSelection();
  const prevLocaleRef = useRef(locale);
  const assetStatesRef = useRef(assetStates);

  // Keep ref updated with latest state
  useEffect(() => {
    assetStatesRef.current = assetStates;
  }, [assetStates]);

  useEffect(() => {
    const prevLocale = prevLocaleRef.current;

    // Only reset if locale actually changed (not on initial mount)
    if (prevLocale && prevLocale !== locale) {
      // Use ref to get CURRENT state, not closure state
      const currentStates = assetStatesRef.current;

      Object.entries(currentStates).forEach(([assetType, assetState]: [string, AssetState]) => {
        const type = assetType as AssetType;

        // Reset BACKGROUND_IMAGE if it's from search
        if (type === AssetType.BACKGROUND_IMAGE && assetState.inputType === InputType.SEARCH) {
          updateAssetState(type, {
            ...getDefaultAssetState(deviceFamily, placement)[AssetType.BACKGROUND_IMAGE],
            key: undefined,
          });
        }

        // Reset LOGO CTA if it's from logo text input
        if (type === AssetType.LOGO && assetState.inputType === InputType.LOGO_TEXT) {
          updateAssetState(type, {
            cta: undefined,
            key: undefined,
            status: AssetStatus.PENDING_INPUT,
          });
        }
      });
    }

    prevLocaleRef.current = locale;
  }, [locale, deviceFamily, placement, updateAssetState]);
};
